export interface SetCookieOptions {
	maxAgeSeconds?: number,
	domain?: string,
	path?: string,
	sameSite?: 'Lax' | 'Strict' | 'None',
	secure?: boolean,
}

const IP_ADDRESS_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/

const PROBE_COOKIE_NAME = '__gmx_domain_probe'

let probeCount = 0

export function getCookieValByName(cookieName: string): string | null {
	const cookie = document.cookie.split('; ').find(row => row.startsWith(`${cookieName}=`))

	if (cookie) {
		// Split only on the first `=`; a value may legally contain more of them.
		return cookie.slice(cookie.indexOf('=') + 1)
	} else {
		return null
	}
}

/**
 * Widest domain a cookie can be scoped to for the current host, so every app on a
 * brand site shares one value (`www.buick.com` and `shop.buick.com` both get `.buick.com`).
 *
 * The answer is found by trying rather than by recognising the suffix.  A cookie scoped
 * to a public suffix — `.com.mx`, `.co.jp`, `.com.cn` — is refused outright, so a write
 * that does not stick is itself the answer.  There are thousands of those suffixes and
 * the list changes over time, which is not something a component library can track.
 *
 * Returns an empty string for hosts that cannot carry a domain-scoped cookie (localhost,
 * IP addresses, single-label hosts), which yields a host-only cookie.  Only pass a
 * hostname the document actually belongs to; the browser refuses anything else.
 */
export function getTopLevelCookieDomain(hostname: string = window.location.hostname): string {
	if (!hostname || hostname === 'localhost' || IP_ADDRESS_PATTERN.test(hostname)) {
		return ''
	}

	const labels = hostname.split('.')

	// Widest candidate first, so the first one the browser keeps is the broadest that works.
	for (let labelCount = 2; labelCount <= labels.length; labelCount++) {
		const candidate = `.${labels.slice(-labelCount).join('.')}`

		if (isDomainAccepted(candidate)) {
			return candidate
		}
	}

	return ''
}

/** Writes a throwaway cookie to find out whether the browser will accept this domain. */
function isDomainAccepted(domain: string): boolean {
	// Unique per attempt so a leftover probe cannot be mistaken for this one.
	const value = `${Date.now()}-${probeCount++}`

	setCookie(PROBE_COOKIE_NAME, value, { domain, maxAgeSeconds: 60 })

	const accepted = getCookieValByName(PROBE_COOKIE_NAME) === value

	deleteCookie(PROBE_COOKIE_NAME, { domain })

	return accepted
}

export function setCookie(cookieName: string, cookieVal: string, options: SetCookieOptions = {}): void {
	const {
		maxAgeSeconds,
		domain,
		path = '/',
		sameSite = 'Lax',
		secure = window.location.protocol === 'https:',
	} = options

	const parts = [`${cookieName}=${cookieVal}`, `path=${path}`, `SameSite=${sameSite}`]

	if (domain) {
		parts.push(`domain=${domain}`)
	}

	if (typeof maxAgeSeconds === 'number') {
		parts.push(`max-age=${maxAgeSeconds}`)
		parts.push(`expires=${new Date(Date.now() + maxAgeSeconds * 1000).toUTCString()}`)
	}

	// Browsers reject `SameSite=None` without `Secure`, dropping the cookie silently.
	if (secure || sameSite === 'None') {
		parts.push('Secure')
	}

	document.cookie = parts.join('; ')
}

export function deleteCookie(cookieName: string, options: Pick<SetCookieOptions, 'domain' | 'path'> = {}): void {
	// Attributes are left at their defaults so the expiry mirrors how the cookie was written.
	setCookie(cookieName, '', { ...options, maxAgeSeconds: 0 })
}
